import type { Scene } from "@/shared/lib/advisorScene";

import { useSkillGrid } from "../model/useSkillGrid";

/**
 * 场景技能条:输入框上方常驻的 1 行 chip(2026-07-15)。
 *
 * <p>★为什么不是复用 SkillGrid:网格只在 `messages.length === 0` 渲染,而场景推荐的**价值时刻恰恰是
 * 「聊到一半、走到海报页、想出图」**,那时 messages.length > 0 恒真 —— 老账号有历史就永远看不到,
 * 旧实现等于只服务全新会话。中途弹整个网格又太侵入,故取 1 行 chip:不打断,常驻,「走到哪推哪」。
 *
 * <p>★安全口径(与 SkillGrid 同):**只对后端已下发的卡做呈现,永不新增、永不解锁**。后端 `/advisor/skills`
 * 已过 role / overlay / 租户 disabled / BuiltinSkillCatalog.HIDDEN 四道闸;scene.skills join 不上就
 * 自然失效(如 assign-task / biz-diagnosis 当前在 HIDDEN 集)—— 哪天恢复上架,前端零改动自动生效。
 *
 * <p>scene 由上层在唤起军师那一刻快照后传入并冻结(开着抽屉切页不重排),此处不自行取路由。
 */
export function SceneSkillBar({
  scene,
  onPick,
}: {
  scene?: Scene | null;
  onPick: (label: string) => void;
}) {
  const { cards, loading } = useSkillGrid();
  if (!scene || loading || cards.length === 0) return null;

  // 按 scene.skills 的优先级排,且**只取后端真下发的**(join 不上 = 没权限/已下架 → 静默丢弃)。
  const picks = scene.skills
    .map((name) => cards.find((c) => c.name === name))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));
  // 一张都没下发 → 整条不渲染:不占地方,也不宣称没做的事。
  if (picks.length === 0) return null;

  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-1.5 px-0.5 pb-1"
      role="group"
      aria-label={`${scene.label}相关技能`}
    >
      <span className="text-xs text-muted-foreground">你在 {scene.label},可以:</span>
      {picks.map((c) => (
        <button
          key={c.name}
          type="button"
          onClick={() => onPick(c.label)}
          className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs text-primary transition hover:bg-primary/10"
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}
